import { useEffect, useRef } from 'react';
import { useGT } from 'gt-react';
import { type FieldProvenance, provenanceFieldLabel } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { useHoverSync } from './HoverSyncContext';

/**
 * Extend-style OCR bounding boxes overlaid on a rendered source page. Thin
 * cobalt outline + faint fill = mapped extracted region, dashed amber = unmapped
 * region; the hovered/active box fills in more + rings so it stands out. A corner
 * tag names the field. Boxes use normalised (0..1) coords positioned as
 * percentages, so they track the page at any render width. Bidirectional
 * hover-sync runs through `HoverSyncContext`: hovering a box highlights its line
 * row / value and vice-versa.
 *
 * Opt-in props (default to the OMS review-page behaviour):
 *  - `labelMode: 'hover'` hides the corner tag until the box is active — keeps
 *    dense extractions readable (the Extend "clean" look).
 *  - `selectable` makes a click pin the box (and scroll it into view), so the
 *    highlight persists while you edit the corresponding value.
 */
export function BoundingBoxOverlay({
  boxes,
  labelMode = 'always',
  selectable = false,
}: {
  boxes: FieldProvenance[];
  labelMode?: 'always' | 'hover';
  selectable?: boolean;
}) {
  const {
    hoveredLineId,
    hoveredBlockId,
    selectedBlockId,
    setHoveredLineId,
    setHoveredBlockId,
    setSelectedBlockId,
  } = useHoverSync();
  const t = useGT();

  const boxRefs = useRef(new Map<string, HTMLDivElement>());

  // When a box is pinned (clicked here or in the value tree), scroll it into
  // view — if it lives on this page's overlay.
  useEffect(() => {
    if (!selectable || !selectedBlockId) return;
    boxRefs.current
      .get(selectedBlockId)
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [selectable, selectedBlockId]);

  // When a line/box is hovered (e.g. the user hovers a line item in the table),
  // bring its source box into view if it's off-screen. `block: 'nearest'` only
  // scrolls when needed, so it's a no-op when the box is already visible (or when
  // the hover originated from hovering the box itself) — no jumpiness.
  useEffect(() => {
    if (!hoveredLineId && !hoveredBlockId) return;
    const targetBlockId =
      hoveredBlockId ??
      boxes.find((b) => b.lineItemId != null && b.lineItemId === hoveredLineId)
        ?.blockId ??
      null;
    if (!targetBlockId) return;
    boxRefs.current.get(targetBlockId)?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'smooth',
    });
  }, [hoveredLineId, hoveredBlockId, boxes]);

  // Paint larger boxes first (underneath) and smaller ones last (on top), so a
  // big region box never sits over the small per-field boxes and swallows their
  // hover — the smallest box at a point wins both the paint and the pointer.
  const orderedBoxes = [...boxes].sort((a, b) => {
    const areaA = a.area ? a.area.width * a.area.height : 0;
    const areaB = b.area ? b.area.width * b.area.height : 0;
    return areaB - areaA;
  });

  return (
    <div className="pointer-events-none absolute inset-0">
      {orderedBoxes.map((box) => {
        if (!box.area) return null;
        const active =
          (box.lineItemId != null && box.lineItemId === hoveredLineId) ||
          (box.blockId != null &&
            (box.blockId === hoveredBlockId ||
              box.blockId === selectedBlockId));
        const tag =
          !box.mapped || box.fieldName == null
            ? t('unmapped region')
            : box.scope === 'lineItem' && box.lineNumber != null
              ? t('line {number} · {field}', {
                  number: String(box.lineNumber + 1),
                  field: provenanceFieldLabel(box.fieldName),
                })
              : provenanceFieldLabel(box.fieldName);
        const showLabel = labelMode === 'always' || active;
        return (
          <div
            key={box.id}
            ref={(el) => {
              if (!box.blockId) return;
              if (el) boxRefs.current.set(box.blockId, el);
              else boxRefs.current.delete(box.blockId);
            }}
            onMouseEnter={() => {
              if (box.lineItemId) setHoveredLineId(box.lineItemId);
              setHoveredBlockId(box.blockId);
            }}
            onMouseLeave={() => {
              setHoveredLineId(null);
              setHoveredBlockId(null);
            }}
            onClick={
              selectable
                ? () =>
                    setSelectedBlockId(
                      box.blockId === selectedBlockId ? null : box.blockId,
                    )
                : undefined
            }
            className={cn(
              'pointer-events-auto absolute rounded-[2px] transition-colors',
              selectable && 'cursor-pointer',
              // Thin outline + a faint fill at rest (kept light so overlapping
              // per-line boxes don't stack into a saturated block); the stronger
              // fill + ring is reserved for hover/active.
              box.mapped
                ? 'border border-blue-500/50 bg-blue-500/[0.03]'
                : 'border border-dashed border-amber-500/60 bg-amber-500/[0.03]',
              active &&
                'border-blue-600 bg-blue-500/10 ring-1 ring-blue-500/70 ring-offset-1',
            )}
            style={{
              left: `${box.area.left * 100}%`,
              top: `${box.area.top * 100}%`,
              width: `${box.area.width * 100}%`,
              height: `${box.area.height * 100}%`,
            }}
            title={tag}
          >
            {showLabel && (
              <span
                className={cn(
                  'absolute -top-[13px] left-0 whitespace-nowrap rounded-sm px-1 text-[8px] leading-[1.5] text-white',
                  box.mapped ? 'bg-blue-600' : 'bg-amber-600',
                )}
              >
                {tag}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
